/**
 * Rebuy Pre-order Validator
 * Sistema para validar mudanças de quantidade no carrinho do Rebuy Engine
 * Aplica regras de pre-order antes de permitir alterações
 */

class RebuyPreorderValidator {
    constructor() {
        this.cache = new Map();
        this.cacheTime = new Map();
        this.apiCalls = new Set();
        this.debounceTimers = new Map();
        this.CACHE_TTL = 10000; // 10 segundos
        this.DEBOUNCE_DELAY = 300; // 300ms

        this.init();
    }

    init() {
        // Aguardar o Rebuy Engine estar disponível
        this.waitForRebuy().then(() => {
            this.setupEventListeners();
        });
    }

    async waitForRebuy() {
        return new Promise((resolve) => {
            const checkRebuy = () => {
                if (window.Rebuy && window.Rebuy.Cart) {
                    resolve();
                } else {
                    setTimeout(checkRebuy, 100);
                }
            };
            checkRebuy();
        });
    }

    setupEventListeners() {
        // Listener para mudanças de quantidade (aumento e diminuição)
        document.addEventListener("rebuy:smartcart.line-item-increase", (event) => {
            this.handleQuantityChange(event, 'increase');
        });

        // Listener para mudanças diretas de quantidade
        document.addEventListener("rebuy:smartcart.line-item-change", (event) => {
            this.handleQuantityChange(event, 'change');
        });
    }

    async handleQuantityChange(event, type) {
        const smartcart = event.detail.smartcart;
        const item = event.detail.item;

        if (!item || !item.variant_id) {
            return;
        }
        
        // Verificar limite de estoque - usar handle se disponível no item
        const stockLimit = await this.getStockLimitFromLocalStorage(item.variant_id, item.handle);
        if (stockLimit && item.quantity >= stockLimit) {            
            // Reverter para quantidade anterior
            this.revertQuantityChange(item, stockLimit);

            // Mostrar feedback para o usuário
            this.showStockLimitMessage(item, stockLimit);

            // Prevenir a mudança
            event.preventDefault();
            event.stopPropagation();
        }
    }

    async getStockLimitFromLocalStorage(variantId, productHandle = null) {
        try {
            const preOrderStockLimits = localStorage.getItem('preOrderStockLimits');

            if (preOrderStockLimits) {
                const limits = JSON.parse(preOrderStockLimits);
                const variantKey = `gid://shopify/ProductVariant/${variantId}`;

                if (limits[variantKey] && limits[variantKey].stockLimit) {
                    return limits[variantKey].stockLimit;
                }
            }

            return await this.fetchStockLimitFromAPI(variantId, productHandle);

        } catch (error) {
            console.error('Erro ao buscar limite de estoque:', error);
            return null;
        }
    }

    async fetchStockLimitFromAPI(variantId, productHandle = null) {
        try {
            const cacheKey = `stock_limit_${variantId}`;
            const cached = this.getCachedData(cacheKey);

            if (cached) {
                return cached;
            }

            if (this.apiCalls.has(variantId)) {
                return null;
            }

            this.apiCalls.add(variantId);

            let variantIds = await this.getVariantIdsFromHandle(variantId, productHandle);
                        
            const url = `https://pre-order-uk-producao-v2mm9.ondigitalocean.app/api/get-product?variantIds=${variantIds}`;     
            const response = await fetch(url);
            const data = await response.json();
            
            if (data && data.result && Array.isArray(data.result) && data.result.length > 0) {
                const validItem = this.findEarliestValidContainerItem(data.result, variantIds, productHandle);
                
                if (validItem && validItem.stockCalc && validItem.stockCalc > 0) {
                    this.setCachedData(cacheKey, validItem.stockCalc);
                    return validItem.stockCalc;
                }
            } else if (data && data.product === "desativado") {
                return null;
            }

            return null;

        } catch (error) {
            console.error('Erro ao buscar limite de estoque via API:', error);
            return null;
        } finally {
            this.apiCalls.delete(variantId);
        }
    }

    async getVariantIdsFromHandle(variantId, productHandle) {
        try {
            // Buscar dados da PDP via handle fornecido
            const pdpData = await this.fetchProductDataFromPDP(productHandle);
            
            if (pdpData && pdpData.variants) {
                // Encontrar a variante correspondente e seus product_childrens
                const variant = pdpData.variants.find(v => v.id == variantId);
                if (variant && variant.product_childrens && Array.isArray(variant.product_childrens)) {
                    // Filtrar IDs vazios e converter para o formato correto
                    const validIds = variant.product_childrens.filter(id => id !== '');
                    if (validIds.length > 0) {
                        return validIds.map(id => `gid://shopify/ProductVariant/${id}`).join(',');
                    }
                }
            }

            // Fallback: usar apenas o variant ID atual
            return `gid://shopify/ProductVariant/${variantId}`;

        } catch (error) {
            console.error('Error getting variant IDs from handle:', error);
            return `gid://shopify/ProductVariant/${variantId}`;
        }
    }

    async fetchProductDataFromPDP(productHandle) {
        try {
            // Buscar a página do produto para obter os dados salvos
            const response = await fetch(`/products/${productHandle}`);
            const html = await response.text();
            
            // Criar um elemento temporário para parsear o HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Buscar o script com type="application/pre-order-json"
            const preOrderScript = doc.querySelector('script[type="application/pre-order-json"]');
            
            if (preOrderScript) {
                try {
                    // Extrair o conteúdo JSON do script
                    const scriptContent = preOrderScript.textContent;
                    // Remover a declaração de variável e obter apenas o JSON
                    const jsonMatch = scriptContent.match(/preOrderData\s*=\s*({[\s\S]*});?/);
                    
                    if (jsonMatch) {
                        const productData = JSON.parse(jsonMatch[1]);
                        return productData;
                    }
                } catch (parseError) {
                    console.error('Error parsing pre-order JSON:', parseError);
                }
            }
            
            // Fallback: buscar o script que contém window.productChildrens (método antigo)
            const scripts = doc.querySelectorAll('script');
            for (let script of scripts) {
                const scriptContent = script.textContent;
                if (scriptContent.includes('window.productChildrens')) {
                    // Executar o script para obter window.productChildrens
                    const scriptElement = document.createElement('script');
                    scriptElement.textContent = scriptContent;
                    document.head.appendChild(scriptElement);
                    
                    // Aguardar um pouco para o script executar
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Verificar se window.productChildrens foi definido
                    if (window.productChildrens) {
                        const productData = window.productChildrens;
                        // Limpar o script temporário
                        document.head.removeChild(scriptElement);
                        return productData;
                    }
                    
                    // Limpar o script temporário
                    document.head.removeChild(scriptElement);
                }
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching product data from PDP:', error);
            return null;
        }
    }

    revertQuantityChange(item, previousQuantity) {
        try {
            const Cart = window.Rebuy.Cart;
            Cart.changeItem({
                id: item.id,
                quantity: previousQuantity
            }, {
                success: () => {
                    console.log('Quantidade revertida com sucesso para:', previousQuantity);
                },
                error: (error) => {
                    console.error('Erro ao reverter quantidade:', error);
                }
            });
        } catch (error) {
            console.error('Erro ao reverter mudança de quantidade:', error);
        }
    }

    updateCartQuantity(variantId, quantity) {
        try {
            const Cart = window.Rebuy.Cart;
            const items = Cart.getItems();
            const item = items.find(item => item.variant_id == variantId);
            
            if (item) {
                Cart.changeItem({
                    id: item.key,
                    quantity: quantity
                }, {
                    success: () => {
                        console.log('Quantidade atualizada para:', quantity);
                    },
                    error: (error) => {
                        console.error('Erro ao atualizar quantidade:', error);
                    }
                });
            }
        } catch (error) {
            console.error('Erro ao atualizar quantidade no carrinho:', error);
        }
    }

    getVariantIdFromItem(itemElement) {
        try {
            // Tentar diferentes seletores para encontrar o variant_id
            const variantIdSelectors = [
                '[data-variant-id]',
                '.rebuy-variant-id',
                '[data-rebuy-variant-id]'
            ];

            for (const selector of variantIdSelectors) {
                const element = itemElement.querySelector(selector);
                if (element) {
                    return element.getAttribute('data-variant-id') || 
                           element.getAttribute('data-rebuy-variant-id') ||
                           element.textContent.trim();
                }
            }

            // Fallback: tentar extrair do ID do item
            const itemId = itemElement.id;
            if (itemId && itemId.includes('variant')) {
                const match = itemId.match(/variant[_-]?(\d+)/i);
                if (match) {
                    return match[1];
                }
            }

            return null;
        } catch (error) {
            console.error('Erro ao obter variant_id do item:', error);
            return null;
        }
    }

    showStockLimitMessage(item, stockLimit) {
        try {
            // Remover mensagens anteriores
            this.removeExistingStockLimitMessages();

            // Criar mensagem de aviso
            const message = document.createElement('div');
            message.className = 'rebuy-stock-limit-message';
            message.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background: #ff6b6b;
                color: white;
                padding: 12px 20px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index: 9999999999;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 14px;
                max-width: 300px;
                animation: slideInRight 0.3s ease-out;
            `;

            message.innerHTML = `
                <div style="display: flex; align-items: center; gap: 8px;">
                    <span style="font-size: 18px;">⚠️</span>
                    <div>
                        <strong>Stock limit reached</strong><br>
                        <small>Maximum quantity allowed: ${stockLimit}</small>
                    </div>
                </div>
            `;

            // Adicionar estilos de animação
            if (!document.getElementById('rebuy-stock-limit-styles')) {
                const style = document.createElement('style');
                style.id = 'rebuy-stock-limit-styles';
                style.textContent = `
                    @keyframes slideInRight {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    @keyframes slideOutRight {
                        from {
                            transform: translateX(0);
                            opacity: 1;
                        }
                        to {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                    }
                `;
                document.head.appendChild(style);
            }

            document.body.appendChild(message);

            // Remover mensagem após 5 segundos
            setTimeout(() => {
                if (message.parentNode) {
                    message.style.animation = 'slideOutRight 0.3s ease-in';
                    setTimeout(() => {
                        if (message.parentNode) {
                            message.parentNode.removeChild(message);
                        }
                    }, 300);
                }
            }, 5000);

        } catch (error) {
            console.error('Erro ao mostrar mensagem de limite de estoque:', error);
        }
    }

    removeExistingStockLimitMessages() {
        const existingMessages = document.querySelectorAll('.rebuy-stock-limit-message');
        existingMessages.forEach(message => {
            if (message.parentNode) {
                message.parentNode.removeChild(message);
            }
        });
    }

    getCachedData(key) {
        const cached = this.cache.get(key);
        const cacheTime = this.cacheTime.get(key);
        
        if (cached && cacheTime && (Date.now() - cacheTime < this.CACHE_TTL)) {
            return cached;
        }
        
        // Limpar cache expirado
        if (cached) {
            this.cache.delete(key);
            this.cacheTime.delete(key);
        }
        
        return null;
    }

    setCachedData(key, data) {
        this.cache.set(key, data);
        this.cacheTime.set(key, Date.now());
    }

    findEarliestValidContainerItem(items, variantIds = null, productHandle = null) {
        if (!Array.isArray(items) || items.length === 0) return null;
        const currentDate = new Date();

        const getChildrenCount = (variantIds) => {
            if (typeof variantIds === 'string' && variantIds.includes(',')) {
                return variantIds.split(',').length;
            }
            return 1;
        };

        const containerGroups = items.reduce((groups, item) => {
            const containerId = item.containerId;
            if (!groups[containerId]) {
                groups[containerId] = [];
            }
            groups[containerId].push(item);
            return groups;
        }, {});

        const validItems = [];

        for (const [containerId, containerItems] of Object.entries(containerGroups)) {
            if (variantIds) {
                const isGotoProduct =
                    productHandle &&
                    (productHandle === 'goto-sofa-three-seater' ||
                        productHandle === 'goto-sofa-loveseat');

                if (!isGotoProduct) {
                    const expectedChildrenCount = getChildrenCount(variantIds);
                    if (containerItems.length !== expectedChildrenCount) {
                        continue;
                    }
                }
            }

            const allItemsValid = containerItems.every(item => {
                if (!item || !item.product || !item.product.container || !item.product.container.ETA) return false;
                if (item.product.container.inUse == false) return false;
                if (item.product.container.statusLocked === true) return false;
                const etaDate = new Date(item.product.container.ETA);
                if (etaDate <= currentDate) return false;

                if (item.product.container.startDate) {
                    const startDate = new Date(item.product.container.startDate);
                    if (startDate > currentDate) return false;
                }
                if (item.product.container.endDate) {
                    const endDate = new Date(item.product.container.endDate);
                    if (endDate < currentDate) return false;
                }
                if (item.parentProduct) {
                    const { stockManagement, inventoryQuantity } = item.parentProduct;
                    if (stockManagement === "outOfStock" && inventoryQuantity > 0) {
                        return false;
                    }
                    if (stockManagement === "forceInStock") {
                        return false;
                    }
                }

                const stock = typeof item.stockCalc === 'number' ? item.stockCalc : 0;
                return stock >= 1;
            });

            if (allItemsValid) {
                validItems.push(...containerItems);
            }
        }
        
        if (validItems.length === 0) return null;
        
        const earliestItem = validItems.reduce((earliest, item) => {
            const itemDate = new Date(item.product.container.ETA);
            const earliestDate = new Date(earliest.product.container.ETA);
            if (itemDate < earliestDate) {
                return item;
            } else if (itemDate.getTime() === earliestDate.getTime()) {
                const itemStock = typeof item.stockCalc === 'number' ? item.stockCalc : Infinity;
                const earliestStock = typeof earliest.stockCalc === 'number' ? earliest.stockCalc : Infinity;
                return itemStock < earliestStock ? item : earliest;
            } else {
                return earliest;
            }
        });

        return earliestItem;
    }

}



// Inicializar quando o DOM estiver pronto
document.addEventListener('DOMContentLoaded', () => {
    new RebuyPreorderValidator();
});

// Também inicializar se o DOM já estiver carregado
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new RebuyPreorderValidator();
    });
} else {
    new RebuyPreorderValidator();
}
